import { ChangeDetectionStrategy, Component, inject, input, Input, numberAttribute, OnInit, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { ButtonModule, IconModule, SelectItem, SelectModule, StrNum } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';

import { PaginationEvent } from '@pp/core/entities';

@Component({
  selector: 'pp-pagination',
  imports: [SelectModule, ReactiveFormsModule, ButtonModule, IconModule, TranslateModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly length = input<number, unknown>(undefined, { transform: numberAttribute });
  readonly page = output<PaginationEvent>();

  pageOptions: SelectItem<StrNum>[];
  form: FormGroup<{ pageSizeOptions: FormControl<number> }>;
  currentPageSize: number;

  @Input({ transform: numberAttribute }) pageIndex = 1;
  @Input({ transform: numberAttribute }) set pageSize(value: number) {
    if (!value) {
      return;
    }

    this.currentPageSize = value;
    this.form?.controls.pageSizeOptions.setValue(value, { emitEvent: false });
  }

  @Input() set pageSizeOptions(sizes: number[]) {
    this.currentPageSize = this.currentPageSize ?? sizes[0];
    this.pageOptions = sizes.map((value: number) => {
      return { id: value, name: value };
    });
  }

  get hasFirst(): boolean {
    return !(this.pageIndex > 1);
  }

  get hasLast(): boolean {
    return this.pageIndex * this.currentPageSize >= this.length();
  }

  get totalCountOfPages(): number {
    return Math.ceil(this.length() / this.currentPageSize);
  }

  get offset(): number {
    const offset = this.pageIndex * this.currentPageSize;

    return Math.min(offset, this.length());
  }

  get index(): number {
    const index = this.pageIndex;

    return Math.min(index, this.totalCountOfPages);
  }

  ngOnInit(): void {
    this.form = this.#fb.group({
      pageSizeOptions: this.currentPageSize,
    });

    const control = this.form.controls.pageSizeOptions;
    control.valueChanges.subscribe(value => {
      this.currentPageSize = value;
      this.#emitEvent();
    });
  }

  next(): void {
    this.pageIndex = this.pageIndex + 1;
    this.#emitEvent();
  }

  toLast(): void {
    this.pageIndex = this.totalCountOfPages;
    this.#emitEvent();
  }

  prev(): void {
    this.pageIndex = this.pageIndex - 1;
    this.#emitEvent();
  }

  toFirst(): void {
    this.pageIndex = 1;
    this.#emitEvent();
  }

  #emitEvent(): void {
    const offset = (this.pageIndex - 1) * this.currentPageSize;

    if (offset >= this.length()) {
      this.pageIndex = this.totalCountOfPages;
    }

    this.page.emit({
      pageSize: this.currentPageSize,
      pageIndex: this.pageIndex,
    });
  }
}
